import {type Dispatch, type SetStateAction, useState} from 'react'

/**
 * Local items derived from a Relay-provided `source`. When `source` changes
 * (Relay returns a new node list), re-runs `derive(source)` and exposes the
 * new value in the same render. Local writes via the setter survive between
 * Relay updates (so drag-and-drop reorders are preserved until the next fetch).
 *
 * `derive` must be referentially stable across renders (wrap in `useCallback`)
 * — the hook only invokes it when `source` changes by reference.
 */
export function usePinnedItems<TSource, TItems>(
  source: TSource | null | undefined,
  derive: (source: TSource | null | undefined) => TItems,
): [TItems, Dispatch<SetStateAction<TItems>>] {
  const [items, setItems] = useState(() => derive(source))
  const [prevSource, setPrevSource] = useState(source)
  if (source !== prevSource) {
    setPrevSource(source)
    if (source) {
      const derived = derive(source)
      setItems(derived)
      return [derived, setItems]
    }
  }
  return [items, setItems]
}
